import { Skeleton, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { Trans } from "react-i18next";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationStatCard } from "web_myplann/Inspiration/Components/InspirationStatCard";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import type { MortgageRateBand } from "web_myplann/Inspiration/Types/MortgageRateBand";
import { getOwnHomeExample } from "web_myplann/Inspiration/Utils/getOwnHomeExample";

const PageResources = Resources.Inspiration.OwnHome;

type Props = {
	rates: MortgageRateBand | null;
};

export const OwnHomeColdIntro: FC<Props> = ({ rates }) => {
	const { t } = useResource();
	const theme = useTheme();
	const formatAmount = useFormatGoalAmount();
	const example = rates ? getOwnHomeExample(rates) : null;

	const cardBg = theme.palette.background.paper;

	const money = (value: number) => formatAmount(value, INSPIRATION_CURRENCY);
	const propertyOnCard =
		example &&
		formatAmount(example.property, INSPIRATION_CURRENCY, {
			millionsUnit: "letter",
		});

	return (
		<>
			<Stack bgcolor={cardBg} px={1.6} py={2}>
				<Typography
					variant="body1"
					color={theme.palette.grey[700]}
					lineHeight={1.625}
				>
					{t(PageResources.introPrefix)}
					<Typography component="span" variant="body1" fontWeight={600}>
						{t(PageResources.introBold)}
					</Typography>
					{t(PageResources.introSuffix)}
				</Typography>
			</Stack>

			<Typography variant="sectionLabel">
				{t(PageResources.exampleTitle)}
			</Typography>

			<Stack bgcolor={cardBg} px={1.6} py={1.6} gap={1.2}>
				{example ? (
					<Typography variant="body2" color={theme.palette.grey[600]}>
						<Trans
							i18nKey={getResourcePath(PageResources.exampleBody)}
							values={{
								rent: money(example.rent),
								property: money(example.property),
							}}
							components={{
								b: <Typography component="span" variant="bodyStrong" tabular />,
								blue: (
									<Typography
										component="span"
										variant="bodyStrong"
										color={theme.palette.blue.main}
										tabular
									/>
								),
							}}
						/>
					</Typography>
				) : (
					<Stack gap={0.4}>
						<Skeleton
							variant="text"
							width="100%"
							height={18}
							animation="wave"
						/>
						<Skeleton variant="text" width="55%" height={18} animation="wave" />
					</Stack>
				)}

				<Stack direction="row" gap={0.8}>
					<InspirationStatCard
						label={t(PageResources.rentCardLabel)}
						amount={money(0)}
						bg={theme.palette.surface.muted}
						labelColor={theme.palette.grey[500]}
						amountColor={theme.palette.red.main}
					/>
					<InspirationStatCard
						label={t(PageResources.ownCardLabel)}
						amount={
							propertyOnCard ? (
								`~${propertyOnCard}`
							) : (
								<Skeleton
									variant="text"
									width={72}
									height={20}
									animation="wave"
								/>
							)
						}
						bg={theme.palette.blue[50]}
						labelColor={theme.palette.blue.main}
						amountColor={theme.palette.blue.main}
					/>
				</Stack>

				<Typography
					variant="caption"
					color={theme.palette.grey[500]}
					textAlign="center"
				>
					{t(PageResources.exampleHint)}
				</Typography>
			</Stack>
		</>
	);
};
